const {
    Enums: { PackageVersionCompatibilityStatus },
} = require("../enums.js");

/**
 * @typedef VersionCompatibilityData
 * @property {string} minimum - The earliest compatible version for this package
 * @property {string} verified - The version of Core this package is confirmed
 * to work with.
 * @property {string} maximum - The latest compatible version for this package
 * @property {number} [status] - Is this package version compatible with
 * whatever core version it's been compared against?
 */

/**
 * Break a version string into numeric parts.
 * @param {string} version - The version to break apart.
 * @returns {number[]} - The version parts, as an array of numbers.
 */
const versionStringToParts = (version) =>
    String(version)
        .replace(/^[v|V]/, "")
        .split(".")
        .map((part) => Number(part));

/**
 * Compares two Foundry VTT versions. This doesn't do semantic versioning
 * comparison, but is limited to Foundry's own version format.
 *
 * Foundry has two formats for version numbers specifically for compatibility
 * checks:
 * - 'Generation': A single digit that indicates 'entire generation', such as
 *   '11' = all v11 versions
 * - 'Build': A 'specific build', such as 11.308, where 308 is the build number.
 *
 * When specifically doing a Foundry compatibility check, you can pass
 * `allowGenerationalEquivalence` as true.
 *
 * In that case, if either 'a' or 'b' is a 'Generation', rather than a 'Build',
 * treat all 'Builds' of that 'Generation' as equal to the 'Generation' for
 * comparison purposes.
 *
 * This is particularly useful for maximum compatibility checks, where 11 might
 * indicate compatibility with 11.308.
 *
 * @param {string} a - The version to compare.
 * @param {string} b - The version that `a` is compared against.
 * @param {boolean} [allowGenerationalEquivalence=false] - Treat 'Generations'
 * as equal to all 'Builds' of that 'Generation'
 * @returns {number} Which version is older? `-1` for `a`, `1` for `b`, `0` for
 * the versions being the same.
 */
function compareFoundryVersions(a, b, allowGenerationalEquivalence = false) {
    const aNumbers = versionStringToParts(a);
    const bNumbers = versionStringToParts(b);
    const maxParts = Math.max(aNumbers.length, bNumbers.length);

    // Compare the parts of the version, starting with generation, then build, eg. 11.308
    for (let idx = 0; idx < maxParts; idx++) {
        const numberA = aNumbers[idx];
        const numberB = bNumbers[idx];

        // Handle missing parts and non-numeric comparisons
        // The first NaN part encountered is considered less than
        if (typeof numberA === "undefined" || Number.isNaN(numberA)) {
            return -1;
        }
        if (typeof numberB === "undefined" || Number.isNaN(numberB)) {
            return 1;
        }
        // Both are decimal values
        const diff = numberA - numberB;
        if (diff < 0) {
            return -1;
        }
        if (diff > 0) {
            return 1;
        }
        // If either 'a' or 'b' is a 'Generation', eg. compareFoundryVersions('10', '10.308', true), then
        // if comparing only generations, and they're the same, stop after checking the first index
        if (allowGenerationalEquivalence && (aNumbers.length === 1 || bNumbers.length === 1)) {
            break;
        }
    }
    return 0;
}

/**
 * Compares the generation of two Foundry VTT versions.
 * @param {String} a
 * @param {String} b
 * @param {Boolean} [asAbsoluteValue=true] Should the difference care about
 * whether one version is older or newer?
 * @throws {TypeError} This function expects that its inputs are Foundry version
 * strings, and will throw if the correct format is not supplied.
 * @returns {Number} The difference in generations between `a` and `b`
 */
function calculateFoundryGenerationDifference(a, b, asAbsoluteValue = true) {
    const aNumber = versionStringToParts(a)[0];
    const bNumber = versionStringToParts(b)[0];
    if (Number.isNaN(aNumber) || Number.isNaN(bNumber)) {
        throw new TypeError("calculateFoundryGenerationDifference expects two Foundry version strings.");
    }
    const diff = aNumber - bNumber;
    return asAbsoluteValue ? Math.abs(diff) : diff;
}

/**
 * Given a package version object, normalize its compatibility data. If the version has a `data` object,
 * i.e. the raw manifest data, use that. Otherwise, use our standardized minCoreVersion, compatibleCoreVersion,
 * and maxCoreVersion fields.
 * @param {unknown} version - Incoming version compatibility data to normalize.
 * @returns {VersionCompatibilityData} The package's compatibility data.
 */
function getCompatibilityDataFromPackageVersionInfo(version) {
    const minimum =
        (version.data && version.data.compatibility && version.data.compatibility.minimum) || version.minCoreVersion;
    const verified =
        (version.data && version.data.compatibility && version.data.compatibility.verified) ||
        version.compatibleCoreVersion;
    const maximum =
        (version.data && version.data.compatibility && version.data.compatibility.maximum) || version.maxCoreVersion;

    return {
        minimum,
        verified,
        maximum,
    };
}

/**
 * Given a core version and a package's compatibility data, determine if the
 * package's compatibility data indicates compatibility with the core version.
 * @param {string} coreVersion The core version to compare against.
 * @param {VersionCompatibilityData} compatibilityData What to compare against
 * the core version.
 * @returns {number} A value between -1 and 1, reflecting whether a package is
 * compatible, partially compatible, or incompatible with the given core version.
 */
function compareCompatibilityDataWithCoreVersion(coreVersion, compatibilityData) {
    const isCompatible = {};

    if (compatibilityData.minimum !== undefined) {
        isCompatible.minimum = compareFoundryVersions(coreVersion, compatibilityData.minimum, true);
    }
    if (compatibilityData.verified !== undefined) {
        isCompatible.verified = compareFoundryVersions(coreVersion, compatibilityData.verified, true);
    }
    if (compatibilityData.maximum !== undefined) {
        isCompatible.maximum = compareFoundryVersions(coreVersion, compatibilityData.maximum, true);
    }

    try {
        /**
         * This package has no minimum, maximum, and verified data. We can't
         * verify if it's compatible/incompatible/risky.
         */
        const doesNotHaveCompatibilityData =
            !compatibilityData.minimum && !compatibilityData.verified && !compatibilityData.maximum;

        /**
         * This package's minimum core version is older than the core version, or
         * its maximum version is newer. The UI should alert the user that this
         * package is incompatible.
         */
        const isIncompatible =
            (typeof isCompatible.minimum === "number" && isCompatible.minimum < 0) ||
            (typeof isCompatible.maximum === "number" && isCompatible.maximum > 0);

        /**
         * The installed core version is at least two generations behind this
         * package's verified core version. The UI should alert the user that this
         * package is (likely) incompatible.
         */
        const isOutdatedByTwoOrMoreVersions =
            compatibilityData.verified &&
            calculateFoundryGenerationDifference(compatibilityData.verified, coreVersion, false) < -1;

        /**
         * The installed core version is one generation behind this
         * package's verified core version. The UI should alert the user that this
         * package is (likely) incompatible.
         */
        const isOutdatedByOneVersion =
            compatibilityData.verified &&
            calculateFoundryGenerationDifference(compatibilityData.verified, coreVersion, false) === -1;

        /**
         * This package has verified version data, and the core version is in the
         * same generation. We can consider this verified as compatible.
         */
        const isVerified =
            compatibilityData.verified &&
            calculateFoundryGenerationDifference(compatibilityData.verified, coreVersion) >= 0;

        if (doesNotHaveCompatibilityData) {
            return PackageVersionCompatibilityStatus.UNKNOWN;
        }
        if (isIncompatible) {
            return PackageVersionCompatibilityStatus.INCOMPATIBLE;
        }
        if (isOutdatedByTwoOrMoreVersions) {
            return PackageVersionCompatibilityStatus.RISKY;
        }
        if (isOutdatedByOneVersion) {
            return PackageVersionCompatibilityStatus.UNKNOWN;
        }

        if (isVerified) {
            return PackageVersionCompatibilityStatus.COMPATIBLE;
        }

        return PackageVersionCompatibilityStatus.UNKNOWN;
    } catch {
        // We can't tell if this is incompatible, possibly due to formatting issues
        // in the version info. We can't be certain about the package's compatibility.
        return PackageVersionCompatibilityStatus.UNKNOWN;
    }
}

/**
 * Given a core version string and a package version object, assemble a
 * normalized compatibility object.
 * @param {string} coreVersion - The core version to compare against.
 * @param {unknown} packageVersion - The package version to build compatibility
 * data for.
 * @returns {VersionCompatibilityData} The package's compatibility data --
 * min, max, verified, and status relative to the provided core version.
 */
function buildCompatibilityData(coreVersion, packageVersion) {
    try {
        const compatibilityData = getCompatibilityDataFromPackageVersionInfo(packageVersion);
        const status = compareCompatibilityDataWithCoreVersion(coreVersion, compatibilityData);
        const result = Object.keys(compatibilityData).reduce((prev, key) => {
            const val = compatibilityData[key];
            prev[key] = !val ? val : String(val);
            return prev;
        }, {});
        result.status = status;
        return result;
    } catch {
        return {};
    }
}

module.exports = {
    compareFoundryVersions,
    calculateFoundryGenerationDifference,
    getCompatibilityDataFromPackageVersionInfo,
    compareCompatibilityDataWithCoreVersion,
    buildCompatibilityData,
};
